/**
 * PATH: apps/hr/src/pages/support/ticket-detail/MentionInput.tsx
 * PURPOSE: Textarea with @mention autocomplete for employees
 */

import { useState, useRef, useCallback } from 'react'
import { AtSign, User } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { Employee, MentionedWorker } from './types'

interface MentionInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  employees: Employee[]
  disabled?: boolean
}

export function MentionInput({ value, onChange, placeholder, employees, disabled }: MentionInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [mentionSearch, setMentionSearch] = useState('')
  const [mentionStartIndex, setMentionStartIndex] = useState(-1)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const filteredEmployees = employees.filter(emp => {
    const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase()
    return fullName.includes(mentionSearch.toLowerCase())
  }).slice(0, 5)

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value; const cursorPos = e.target.selectionStart
    onChange(newValue)
    const textBeforeCursor = newValue.substring(0, cursorPos); const lastAtIndex = textBeforeCursor.lastIndexOf('@')
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1)
      if (!textAfterAt.includes(' ') || textAfterAt.split(' ').length <= 2) { setMentionSearch(textAfterAt); setMentionStartIndex(lastAtIndex); setShowSuggestions(true); setSelectedIndex(0); return }
    }
    setShowSuggestions(false)
  }, [onChange])

  const selectEmployee = useCallback((emp: Employee) => {
    const fullName = `${emp.first_name} ${emp.last_name}`
    const beforeMention = value.substring(0, mentionStartIndex)
    const afterCursor = textareaRef.current ? value.substring(textareaRef.current.selectionStart) : ''
    onChange(`${beforeMention}@${fullName} ${afterCursor}`)
    setShowSuggestions(false)
    setTimeout(() => { if (textareaRef.current) { const pos = beforeMention.length + fullName.length + 2; textareaRef.current.focus(); textareaRef.current.setSelectionRange(pos, pos) } }, 0)
  }, [value, mentionStartIndex, onChange])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showSuggestions || filteredEmployees.length === 0) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(p => Math.min(p + 1, filteredEmployees.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(p => Math.max(p - 1, 0)) }
    else if (e.key === 'Enter' && showSuggestions) { e.preventDefault(); selectEmployee(filteredEmployees[selectedIndex]) }
    else if (e.key === 'Escape') setShowSuggestions(false)
  }, [showSuggestions, filteredEmployees, selectedIndex, selectEmployee])

  return (
    <div className="relative">
      <textarea ref={textareaRef} value={value} onChange={handleChange} onKeyDown={handleKeyDown} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} placeholder={placeholder} disabled={disabled} rows={2} className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-teal-500" />
      {showSuggestions && filteredEmployees.length > 0 && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden z-10 max-h-48 overflow-y-auto">
          <div className="px-3 py-1.5 text-xs text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-700 flex items-center gap-1.5"><AtSign className="w-3 h-3" />Tag an employee</div>
          {filteredEmployees.map((emp, index) => (
            <button key={emp.id} onClick={() => selectEmployee(emp)} className={cn('w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700', index === selectedIndex && 'bg-slate-100 dark:bg-slate-700')}>
              <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-xs font-medium">{emp.first_name[0]}{emp.last_name[0]}</div>
              <span className="text-sm text-slate-900 dark:text-white">{emp.first_name} {emp.last_name}</span>
            </button>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400 mt-1">Type @ to mention someone</p>
    </div>
  )
}

/** Render comment content with highlighted @mentions */
export function renderContentWithMentions(content: string, mentions?: MentionedWorker[]) {
  if (!mentions || mentions.length === 0) return <span>{content}</span>
  const mentionMap = new Map(mentions.map(m => [m.mention_text, m]))
  const parts: React.ReactNode[] = []; let lastIndex = 0
  const mentionRegex = /@(?:"([^"]+)"|([A-Za-z]+(?:\s+[A-Za-z]+)?))/g; let match
  while ((match = mentionRegex.exec(content)) !== null) {
    const mentionText = `@${match[1] || match[2]}`; const worker = mentionMap.get(mentionText)
    if (match.index > lastIndex) parts.push(<span key={`text-${lastIndex}`}>{content.substring(lastIndex, match.index)}</span>)
    if (worker) parts.push(<span key={`mention-${match.index}`} className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 rounded text-xs font-medium"><User className="w-3 h-3" />{worker.first_name} {worker.last_name}</span>)
    else parts.push(<span key={`mention-${match.index}`} className="text-teal-600">{mentionText}</span>)
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < content.length) parts.push(<span key={`text-${lastIndex}`}>{content.substring(lastIndex)}</span>)
  return <>{parts}</>
}
